import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';

// Live view for one attendance point — frame + zone + skeleton on the left, a
// big-font readout on the right, in the same language as PatrolLiveView.
//
// It reads from BOTH sides and shows them together, because either one alone
// misleads:
//   /ui/patrol/test      what the camera can see right now (pose, zone, face).
//                        Runs pose per poll, so it is opt-in via `active`.
//   /ui/attendance/live  what the ENGINE has decided (presence, phase, done).
//
// The patrol view shows a gesture countdown because a raised hand is how a guard
// checks in. Attendance is the opposite: a recognized employee is punched on the
// walk-through and never raises anything. So the hand row here is labelled as
// the EXCEPTION route and only lights up once recognition has already failed —
// showing it as a step would train employees to stop and wave at every gate.

const COCO_EDGES = [[5, 7], [7, 9], [6, 8], [8, 10], [5, 6], [5, 11], [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16]];

export default function AttendanceLiveView({
  cameraIdx, checkpointId, polygon = [], marked = false,
  minConf = 0.5, holdSeconds = 1.5, active = true, onboarded = null,
}) {
  const [imgUrl, setImgUrl] = useState('');
  const [readout, setReadout] = useState(null);   // camera truth
  const [cp, setCp] = useState(null);             // engine truth
  const imgRef = useRef(null), canvasRef = useRef(null), busy = useRef(false);
  const hasCam = cameraIdx !== undefined && cameraIdx !== null && cameraIdx !== '';
  const polyKey = JSON.stringify(polygon);

  const refreshFrame = useCallback(async () => {
    if (!hasCam) return;
    try { setImgUrl(await apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`)); } catch { /* keep last */ }
  }, [cameraIdx, hasCam]);

  useEffect(() => { if (!hasCam) return; refreshFrame(); const t = setInterval(refreshFrame, 800); return () => clearInterval(t); }, [refreshFrame, hasCam]);
  useEffect(() => { setImgUrl(''); setReadout(null); setCp(null); }, [cameraIdx, checkpointId]);

  // Camera readout — pose is expensive, so skip it entirely unless mounted-and-active.
  useEffect(() => {
    if (!hasCam || !active) return;
    let alive = true;
    const poll = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const r = await apiFetch('/ui/patrol/test', { method: 'POST', json: { camera_idx: Number(cameraIdx), polygon } });
        if (!alive) return;
        setReadout(r.ok ? await r.json() : null);
      } catch { /* transient */ }
      finally { busy.current = false; }
    };
    poll();
    const t = setInterval(poll, 700);
    return () => { alive = false; clearInterval(t); };
  }, [cameraIdx, polyKey, hasCam, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Engine state — cheap read of session state, no detection.
  useEffect(() => {
    if (!checkpointId) return;
    let alive = true;
    const poll = () => apiFetch(`/ui/attendance/live?cp_id=${encodeURIComponent(checkpointId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive && d) setCp((d.checkpoints || [])[0] || null); })
      .catch(() => {});
    poll();
    const t = setInterval(poll, 1000);
    return () => { alive = false; clearInterval(t); };
  }, [checkpointId]);

  const inZone     = readout?.in_zone ?? cp?.in_zone ?? false;
  const raised     = !!(readout?.left_raised || readout?.right_raised);
  const faceName   = readout?.face_name || null;
  const faceConf   = readout?.face_conf;
  // The engine ALSO refuses a match whose name is not an onboarded employee.
  // Judging on name and score alone showed a green tick for a face the engine
  // was silently rejecting — the panel has to apply every gate the engine does,
  // or it becomes a second opinion rather than a view. `onboarded` is null when
  // the caller has no directory to check against, which means "don't gate".
  const strong     = !!(readout?.face_status === 'VERIFIED' && faceName && Number(faceConf ?? 0) >= minConf);
  const notEnrolled = !!(strong && onboarded && !onboarded.includes(faceName));
  const recognized = strong && !notEnrolled;
  const weak       = !!(faceName && !strong);
  // A punch, and ONLY a punch. `event_done` also covers "absorbed by dedup" and
  // "filed as an exception", and treating those as done showed a green MARKED
  // for a visit where nothing was recorded — the most misleading thing this
  // panel could possibly say.
  const done       = marked || cp?.outcome === 'PUNCHED' || cp?.phase === 'DONE';
  const absorbed   = !done && cp?.outcome === 'ABSORBED';
  const excepted   = !done && cp?.outcome === 'EXCEPTION';

  const draw = useCallback(() => {
    const cv = canvasRef.current, img = imgRef.current;
    if (!cv || !img) return;
    cv.width = img.clientWidth; cv.height = img.clientHeight;
    const ctx = cv.getContext('2d'); const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    if (polygon.length >= 3) {
      ctx.beginPath();
      polygon.forEach(([x, y], i) => { const px = x * W, py = y * H; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
      ctx.closePath();
      ctx.strokeStyle = done ? '#5cc98a' : inZone ? '#ffd24d' : '#4da3ff';
      ctx.fillStyle   = done ? 'rgba(92,201,138,0.20)' : inZone ? 'rgba(255,210,77,0.18)' : 'rgba(77,163,255,0.12)';
      ctx.lineWidth = 2; ctx.fill(); ctx.stroke();
    }
    (readout?.persons || []).forEach(p => {
      const kp = p.kpts; ctx.strokeStyle = '#ffd24d'; ctx.lineWidth = 2;
      COCO_EDGES.forEach(([a, b]) => {
        if (kp[a][2] >= 0.3 && kp[b][2] >= 0.3) {
          ctx.beginPath(); ctx.moveTo(kp[a][0] * W, kp[a][1] * H); ctx.lineTo(kp[b][0] * W, kp[b][1] * H); ctx.stroke();
        }
      });
      ctx.fillStyle = '#ffd24d';
      kp.forEach(k => { if (k[2] >= 0.3) { ctx.beginPath(); ctx.arc(k[0] * W, k[1] * H, 3, 0, 6.2832); ctx.fill(); } });
    });
  }, [polygon, readout, inZone, done]);

  useEffect(() => { draw(); }, [draw, imgUrl]);

  // Verdict mirrors the engine's own two paths, in the order it evaluates them.
  const verdict = done                 ? { label: '✓ MARKED',              tone: 'done' }
    : absorbed                         ? { label: 'ALREADY COUNTED TODAY', tone: 'idle' }
    : excepted                         ? { label: 'SENT FOR REVIEW',       tone: 'warn' }
    : recognized                       ? { label: `✓ ${faceName}`,          tone: 'done' }
    : notEnrolled                      ? { label: `${faceName} NOT ONBOARDED`, tone: 'warn' }
    : !inZone                          ? { label: 'STEP INTO THE ZONE',     tone: 'idle' }
    : raised                           ? { label: 'EXCEPTION — FOR REVIEW', tone: 'warn' }
    : weak                             ? { label: `TOO LOW TO MARK (${Number(faceConf).toFixed(2)} < ${minConf})`, tone: 'warn' }
    :                                    { label: 'NOT RECOGNISED — RAISE HAND', tone: 'warn' };

  const toneBg = { done: 'rgba(92,201,138,0.92)', warn: 'rgba(255,210,77,0.90)', idle: 'rgba(40,40,40,0.6)' };
  const toneBd = { done: '#5cc98a', warn: '#ffd24d', idle: 'var(--border)' };

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ position: 'relative', flex: '1 1 340px', maxWidth: 640, background: '#000', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {imgUrl
          ? <img ref={imgRef} src={imgUrl} alt="attendance point" onLoad={draw} style={{ display: 'block', width: '100%', height: 'auto' }} />
          : <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Loading camera…</div>}
        <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
      </div>

      <div style={{ flex: '0 0 250px', minWidth: 230 }}>
        <Row label="Person detected" ok={readout?.person_detected} value={readout?.person_detected ? 'YES' : 'NO'} />
        <Row label="Inside zone"     ok={inZone}                   value={inZone ? 'YES' : 'NO'} />
        <Row label="Face"
             ok={recognized ? true : ((weak || notEnrolled) ? false : undefined)}
             value={faceName ? `${faceName}${faceConf != null ? ` ${Number(faceConf).toFixed(2)}` : ''}` : (readout?.face_status ? 'Unknown' : '—')} />
        {/* Only meaningful once recognition has failed — that is the sole path
            on which a raised hand does anything at all. */}
        <Row label="Hand raised (exception)" ok={raised || null} value={raised ? 'RAISED' : '—'} />
        <Row label="Zone presence" ok={(cp?.presence_pct ?? 0) >= 100 || null}
             value={`${(cp?.presence_elapsed ?? 0).toFixed(1)} / ${Number(cp?.presence_required ?? 0).toFixed(1)} s`} />

        <div style={{
          marginTop: 14, padding: '12px 14px', borderRadius: 10, textAlign: 'center',
          fontSize: 16, fontWeight: 800, letterSpacing: 0.4,
          background: toneBg[verdict.tone], color: verdict.tone === 'idle' ? 'var(--text-muted)' : '#08130c',
          border: `1px solid ${toneBd[verdict.tone]}`,
        }}>{verdict.label}</div>

        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
          Recognised faces are marked automatically. A raised hand is only needed
          when recognition fails — it files the attempt for review, it does not
          mark attendance. Below {minConf} confidence counts as not recognised.
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, ok }) {
  const color = ok === true ? '#5cc98a' : ok === false ? '#e07070' : 'var(--text)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 15, color }}>{value ?? '—'}</span>
    </div>
  );
}
